import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import { Page } from 'Components';
import { isFloatingSidebarRoute } from 'Components/FloatingSidebar';
import { AppContext, AuthContext } from 'Context';
import { Pathname } from 'Routes';
import { useNewUI } from 'hooks/useNewUI';
import { isAffiliateUser } from 'Utils/settingsAccess';
import { V2ThemeProvider } from './V2Layout';

const ICON_MAP = {
  account: PersonOutlineRoundedIcon,
  cloud: CloudOutlinedIcon,
  notifications: NotificationsNoneRoundedIcon,
  billing: PaymentsOutlinedIcon,
  privacy: LockOutlinedIcon,
  manageUsers: GroupOutlinedIcon,
  affiliate: HandshakeOutlinedIcon,
  referrals: PersonAddAltRoundedIcon,
  terms: GavelRoundedIcon,
  subscriber: DescriptionOutlinedIcon,
  tvLogin: TvRoundedIcon,
};

// Width of the v2 global sidebar at each breakpoint
// (must match SIDEBAR_WIDTH_PX in V2Sidebar.jsx).
// Below 900px the global sidebar collapses to a drawer, so no offset.
const SIDEBAR_WIDTH = 250;
// Detail pages have no left rail any more, so header + content are simply
// centered at this width (matches the settings landing) instead of being
// offset by a sidebar reservation.
const CONTENT_MAX_WIDTH = 1200;

// Below this width fitViewport pages fall back to normal scrolling — there
// isn't enough room to lay content out side by side.
const FIT_MIN_WIDTH = '(min-width: 1200px)';

// Space the fixed bottom dock occupies (68px panel + 8px bottom offset in
// Dock.css) plus a gap, so fitViewport content stops above it. Only reserved
// where a dock is actually rendered — see dockPresent below.
const DOCK_RESERVE = 92;

// Must match the container's `pt` at md+, the common header's `mb`, and the
// gap left under the sidebar — the fixed sidebar is positioned from these.
const CONTENT_TOP_GAP = 24;
const HEADER_GAP = 16;
const BOTTOM_GAP = 24;

const SettingsShellInner = ({
  title, description, children, fitViewport = false, headerAction = null, hideQuickLinks = false,
  // Opt out of the floating bottom dock (e.g. /tv-login, where the client wants
  // the screen free of the tab bar). Also drops the reserved space under it.
  hideDock = false,
}) => {
  const {
    langText, getAccessChecker, setTopOrBottom, apiResponse,
    userAccessedData, managerPrivatePlanAccess, userDetails,
  } = useContext(AppContext);
  const { isInActiveUser } = useContext(AuthContext);
  const { themeMode, toggleTheme } = useNewUI();
  const sideBarText = langText?.settings;
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const isMobile = useMediaQuery('(max-width:899px)');

  const containerRef = useRef(null);
  // Distance from the document top to the page container. Measured on every
  // settings page (not just fitViewport ones) because --header-height alone
  // understates it — there's a strip above the app header.
  const [topOffset, setTopOffset] = useState(null);
  const [fitActive, setFitActive] = useState(false);
  const [wideActive, setWideActive] = useState(false);
  const fitHeight = fitActive && topOffset !== null
    ? `calc(100vh - ${topOffset}px)`
    : null;
  // Every other settings page is pinned to the viewport too, so the app navbar
  // and the "Settings" heading stay put and the sidebar keeps its full height —
  // only the content column scrolls. Same height maths as fitViewport; the
  // difference is that this branch owns the content column's inner scrolling,
  // whereas fitViewport pages lay their own content out to fit.
  const pinHeight = !fitViewport && wideActive && topOffset !== null
    ? `calc(100vh - ${topOffset}px)`
    : null;
  const pageHeight = fitHeight || pinHeight;

  // Routes with the floating LEFT rail (settings included) render no bottom
  // dock, so reserving DOCK_RESERVE under them left an ~68px dead band that
  // clipped the last thing on a pinned page — the section shortcut row.
  const dockPresent = !hideDock && !isFloatingSidebarRoute(location.pathname);

  // The common header sits above the sidebar/content row, so the fixed
  // sidebar has to be pushed down by however tall it renders.
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const read = () => setHeaderHeight(el.offsetHeight);
    read();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setTopOrBottom(true);
    return () => setTopOrBottom(false);
  }, [setTopOrBottom]);

  // Measure where the page container actually starts, so the fixed sidebar
  // lines up with the content on every page. On fitViewport pages the same
  // measurement pins the height and locks document scrolling.
  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;

    const unlock = () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };

    const measure = () => {
      // Wide desktops pin every settings page; narrower screens keep the
      // ordinary document flow, where there isn't room to sit side by side.
      const wide = window.matchMedia(FIT_MIN_WIDTH).matches;
      // Unlock first so the page is measurable in its natural state.
      unlock();
      if (wide) window.scrollTo(0, 0);
      const rect = containerRef.current?.getBoundingClientRect();
      const top = rect ? rect.top + window.scrollY : 0;
      setTopOffset(Math.max(0, Math.round(top)));
      setWideActive(wide);
      setFitActive(fitViewport && wide);
      if (wide) {
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
      }
    };

    measure();
    // Re-measure once the header has settled at its final height.
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      unlock();
    };
  }, [fitViewport]);

  const showCloudOptions = apiResponse?.data?.[0]?.private_plan_access === '1'
    || apiResponse?.data?.[0]?.private_plan_access === 1
    || (userAccessedData && managerPrivatePlanAccess);

  const allLinks = useMemo(() => {
    const account = { key: 'account', name: sideBarText?.accountSideBarTitle || 'Account', link: Pathname.getSettingsPath(Pathname.settings.accounts) };
    const cloud = { key: 'cloud', name: sideBarText?.cloudSidebarTitle || 'Cloud', link: Pathname.getSettingsPath(Pathname.settings.cloud) };
    const notifications = { key: 'notifications', name: sideBarText?.notificationSidebarTitle || 'Notifications', link: Pathname.getSettingsPath(Pathname.settings.notifications) };
    const billing = { key: 'billing', name: 'Billing & Payments', link: Pathname.getSettingsPath(Pathname.settings.billings) };
    const privacy = { key: 'privacy', name: sideBarText?.privacySidebarTitle || 'Privacy', link: Pathname.getSettingsPath(Pathname.settings.privacy) };
    const manageUsers = { key: 'manageUsers', name: 'User Management', link: Pathname.getSettingsPath(Pathname.settings.manageUsers) };
    // Affiliate accounts (allow-list in Utils/settingsAccess) get the Affiliate
    // Program entry INSTEAD of User Management; everyone else keeps the latter.
    const affiliate = { key: 'affiliate', name: 'Affiliate Program', link: Pathname.getSettingsPath(Pathname.settings.affiliate), badge: 'New' };
    const usersEntry = isAffiliateUser(userDetails?.email) ? affiliate : manageUsers;
    const referrals = { key: 'referrals', name: 'Referrals', link: Pathname.getSettingsPath(Pathname.settings.referrals) };
    const terms = { key: 'terms', name: sideBarText?.termsSidebarTitle || 'Terms of Use', link: Pathname.getSettingsPath(Pathname.settings.termsAndConditions) };
    const subscriber = { key: 'subscriber', name: sideBarText?.subscriberSidebarTitle || 'Subscriber Agreement', link: Pathname.getSettingsPath(Pathname.settings.subscriberAgreement) };
    // TV login lives at a top-level path (/tv-login), not under /settings.
    const tvLogin = { key: 'tvLogin', name: 'TV & devices', link: Pathname.tvLogin };

    if (isInActiveUser) {
      return [billing, privacy, terms, subscriber];
    }
    if (getAccessChecker?.success === true) {
      return showCloudOptions
        ? [account, cloud, notifications, billing, privacy, tvLogin, terms, subscriber]
        : [account, notifications, billing, privacy, tvLogin, terms, subscriber];
    }
    // Referrals only for ordinary owner sessions — the classic settings sidebar
    // left it out of the inactive-user and access/impersonation lists above.
    return showCloudOptions
      ? [account, cloud, notifications, billing, privacy, usersEntry, referrals, tvLogin, terms, subscriber]
      : [account, notifications, billing, privacy, usersEntry, referrals, tvLogin, terms, subscriber];
  }, [sideBarText, isInActiveUser, getAccessChecker, showCloudOptions, userDetails]);

  const showQuickLinks = !hideQuickLinks && allLinks.length > 1;
  // The shortcut row carries its own trailing space, so a pinned page that
  // renders it doesn't hold BOTTOM_GAP back on top — that double gap is what
  // left the row grazing the clip edge with no slack for the first-paint
  // measurement to settle into.
  const bottomReserve = dockPresent
    ? DOCK_RESERVE
    : (showQuickLinks ? 8 : BOTTOM_GAP);

  const viewportHeight = 'calc(100vh - var(--header-height, 64px))';

  return (
    <Page noBottomBar noFixedSidebar={hideDock}>
      <Box
        ref={containerRef}
        sx={{
          // On wide desktops the page occupies exactly one screen, so pin the
          // measured height and clip instead of letting the document scroll —
          // that keeps the navbar and the heading in place. Narrower screens
          // keep the scrolling flow.
          minHeight: pageHeight ? 0 : viewportHeight,
          height: pageHeight || 'auto',
          overflow: pageHeight ? 'hidden' : 'visible',
          // When pinned, the column chain below stretches children to the
          // bottom of the pinned height.
          display: pageHeight ? 'flex' : 'block',
          flexDirection: 'column',
          // Transparent in light mode so the Page soft-aurora shows through;
          // keep a solid base in dark mode.
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.background.default : 'transparent'),
          color: 'text.primary',
          width: '100%',
          // Nav lives in the bottom dock (no left rail), so no left reservation —
          // symmetric gutters instead.
          pl: { xs: 0, md: 2 },
          pr: { xs: 0, md: 2 },
          pt: { xs: 2, md: 3 },
          pb: pageHeight ? `${bottomReserve}px` : 6,
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        {/* Common settings header: one title + subtext for every section,
            rendered above the sidebar and the content column. The per-section
            title/description block is commented out further down. */}
        <Box
          ref={headerRef}
          sx={{
            // Centered to line up with the content column below.
            width: '86%',
            maxWidth: 'none',
            mx: 'auto',
            px: { xs: '12px', md: '20px' },
            mb: 2,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 1,
            flexShrink: 0,
            // /settings is a FloatingSidebar route — the rail already reserves
            // its own space via <Page>'s .withRail (this shell's outer Box pl
            // above). Centering at 86% on top of that stacks an extra gutter
            // and leaves content starting well right of the rail instead of
            // flush with it, like every other rail page.
            '@media (min-width: 1025px)': { width: '100%', maxWidth: '100%', mx: 0 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              component="button"
              type="button"
              onClick={() => navigate(Pathname.settings.basePath)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 1,
                ml: -0.75,
                px: 1,
                py: 0.5,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'text.secondary',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'inherit',
                borderRadius: 1,
                transition: 'background-color 0.15s ease, color 0.15s ease',
                '& svg': { transition: 'transform 0.2s ease' },
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                '&:hover svg': { transform: 'translateX(-2px)' },
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m15 18-6-6 6-6" />
              </svg>
              Back to Settings
            </Box>
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                fontSize: { xs: 22, sm: 26, md: 30 },
              }}
            >
              {title || 'Settings'}
            </Typography>
            {description ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {description}
              </Typography>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
            {headerAction}
            {isMobile && (
              <Tooltip title={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}>
                <IconButton size="small" onClick={toggleTheme}>
                  {themeMode === 'dark' ? (
                    <LightModeRoundedIcon fontSize="small" />
                  ) : (
                    <DarkModeRoundedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Box
          component="aside"
          sx={{
            // Sidebar removed on detail pages — the header back button returns
            // to the settings grid instead.
            display: 'none',
            mx: 2,
            mb: 2,
            p: 2,
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Redundant now that the common header above says "Settings".
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', px: 1.25, display: 'block' }}
          >
            Settings
          </Typography>
          */}
          <Stack spacing={0.5}>
            {allLinks.map(({ key, name, link, badge }) => {
              const Icon = ICON_MAP[key] || PersonOutlineRoundedIcon;
              const active = currentPath === link;
              return (
                <NavLink key={key} to={link} style={{ textDecoration: 'none' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1.1,
                      borderRadius: 2,
                      cursor: 'pointer',
                      color: active ? 'primary.main' : 'text.primary',
                      bgcolor: active ? 'action.hover' : 'transparent',
                      fontWeight: active ? 600 : 500,
                      fontSize: 14,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                    <Box component="span">{name}</Box>
                    {badge && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.75,
                          px: 0.75,
                          py: '1px',
                          borderRadius: '999px',
                          bgcolor: 'rgba(90,165,239,0.15)',
                          color: '#5aa5ef',
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          lineHeight: 1.4,
                        }}
                      >
                        {badge}
                      </Box>
                    )}
                  </Box>
                </NavLink>
              );
            })}
          </Stack>
        </Box>

        <Box
          sx={{
            // Centered content column (no left rail any more) — same width as the
            // header above so they line up on every settings page.
            width: '86%',
            maxWidth: 'none',
            mx: 'auto',
            px: { xs: '12px', md: '20px' },
            ...(fitHeight
              ? {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }
              : {
                display: { xs: 'block', md: 'flex' },
                alignItems: pinHeight ? 'stretch' : { md: 'flex-start' },
                gap: { md: 2 },
                ...(pinHeight && { flex: 1, minHeight: 0 }),
              }),
            // /settings IS a FloatingSidebar route — see the matching note on
            // the header Box above.
            '@media (min-width: 1025px)': { width: '100%', maxWidth: '100%', mx: 0 },
          }}
        >
          <Box
            component="aside"
            sx={{
              display: 'none',
              flexDirection: 'column',
              width: SIDEBAR_WIDTH,
              flexShrink: 0,
              ...(fitHeight
                // fitViewport pages lock document scrolling, so the sidebar is
                // pinned to the viewport and stretched to fill the visible page
                // area: page offset + the container's top padding + the common
                // header + its bottom margin. Falls back to --header-height for
                // the first paint, before measuring.
                ? {
                  position: 'fixed',
                  top: topOffset === null
                    ? `calc(var(--header-height, 64px) + ${CONTENT_TOP_GAP + headerHeight + HEADER_GAP}px)`
                    : `${topOffset + CONTENT_TOP_GAP + headerHeight + HEADER_GAP}px`,
                  left: { md: 'calc(104px + 16px)', lg: 'calc(108px + 16px)', xl: 'calc(116px + 16px)' },
                  // Fill the rest of the visible page height, leaving a gap at
                  // the bottom that matches the gap above it.
                  height: topOffset === null
                    ? `calc(100vh - var(--header-height, 64px) - ${CONTENT_TOP_GAP + headerHeight
                      + HEADER_GAP + BOTTOM_GAP}px)`
                    : `calc(100vh - ${topOffset + CONTENT_TOP_GAP + headerHeight
                      + HEADER_GAP + BOTTOM_GAP}px)`,
                  overflowY: 'auto',
                  zIndex: 5,
                }
                // In normal flow beside the content. When pinned it takes the
                // exact same height as the fitViewport (Account) sidebar above,
                // so every settings page matches: from the top of the content
                // row down to BOTTOM_GAP above the viewport bottom. That runs
                // past the container's bottom dock padding on purpose — overflow
                // clips at the padding edge, so it stays visible. Below the pin
                // breakpoint the page scrolls and the card hugs its links.
                : {
                  position: 'static',
                  ...(pinHeight && {
                    alignSelf: 'flex-start',
                    height: topOffset === null
                      ? `calc(100vh - var(--header-height, 64px) - ${CONTENT_TOP_GAP + headerHeight
                        + HEADER_GAP + BOTTOM_GAP}px)`
                      : `calc(100vh - ${topOffset + CONTENT_TOP_GAP + headerHeight
                        + HEADER_GAP + BOTTOM_GAP}px)`,
                    minHeight: 0,
                    overflowY: 'auto',
                  }),
                }),
              p: 2,
              borderRadius: 3,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(15,17,28,0.04)',
            }}
          >
            {/* Redundant now that the common header above says "Settings".
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', px: 1.25, display: 'block' }}
            >
              Settings
            </Typography>
            */}
            <Stack
              spacing={0.5}
              sx={{ flex: 1 }}
            >
              {allLinks.map(({ key, name, link, badge }) => {
                const Icon = ICON_MAP[key] || PersonOutlineRoundedIcon;
                const active = currentPath === link;
                return (
                  <NavLink
                    key={key}
                    to={link}
                    style={{ textDecoration: 'none' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1.1,
                        borderRadius: 2,
                        cursor: 'pointer',
                        color: active ? 'primary.main' : 'text.primary',
                        bgcolor: active ? 'action.hover' : 'transparent',
                        fontWeight: active ? 600 : 500,
                        fontSize: 14,
                        transition: 'background-color 120ms ease',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }} />
                      <Box component="span">{name}</Box>
                      {badge && (
                        <Box
                          component="span"
                          sx={{
                            ml: 0.75,
                            px: 0.75,
                            py: '1px',
                            borderRadius: '999px',
                            bgcolor: 'rgba(90,165,239,0.15)',
                            color: '#5aa5ef',
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            lineHeight: 1.4,
                          }}
                        >
                          {badge}
                        </Box>
                      )}
                    </Box>
                  </NavLink>
                );
              })}
            </Stack>
          </Box>

          <Box
            sx={{
              minWidth: 0,
              ...(fitHeight
                ? {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }
                : {
                  // Take the width the in-flow sidebar leaves behind.
                  flex: { md: 1 },
                  // Pinned: this is the only thing that scrolls. 4px of right
                  // padding keeps card shadows clear of the scrollbar.
                  ...(pinHeight && { minHeight: 0, overflowY: 'auto', pr: 0.5 }),
                }),
            }}
          >
            {/* Per-section header (title + description). Replaced for now by
                the common settings header above; kept here so it can be
                switched back on. `title`/`description` props are still passed
                in by every settings page.
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{
                mb: fitHeight ? 2 : 3,
                alignItems: { sm: 'flex-end' },
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                    fontSize: { xs: 22, sm: 26, md: 30 },
                  }}
                >
                  {title}
                </Typography>
                {description && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {description}
                  </Typography>
                )}
              </Box>
              {isMobile && (
                <Stack direction="row" spacing={1}>
                  <Tooltip title={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}>
                    <IconButton size="small" onClick={toggleTheme}>
                      {themeMode === 'dark' ? (
                        <LightModeRoundedIcon fontSize="small" />
                      ) : (
                        <DarkModeRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
            */}
            <Box
              sx={fitHeight
                ? {
                  flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
                }
                : { minWidth: 0 }}
            >
              {children}

              {/* Quick shortcuts to the other settings sections (the sidebar is
                  gone on detail pages). Suppressed on pages that opt out via
                  hideQuickLinks — only sub-flows that take over the whole page
                  (e.g. billing's Manage Payment Info) do that. */}
              {showQuickLinks && (
                <Box sx={{
                  // On fit-height pages the content column is a flex column, so
                  // mt:auto pins this "Looking for something else?" divider+links
                  // block to the bottom even when the content above sits at the
                  // top. Non-fit pages flow normally, so keep the small top gap.
                  mt: fitHeight ? 'auto' : 1, pt: 1, pb: 0.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, minWidth: 0,
                }}
                >
                  {/* The label rides on the same wrapping line as the chips.
                      Stacked above them it cost the block ~36px of height, which
                      was enough to push the whole row under the fold on the
                      shorter settings pages. Chips wrap rather than scroll
                      sideways — that scrollbar is hidden until you scroll on
                      macOS, so the last chips read as simply missing. */}
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ minWidth: 0, rowGap: 0.75, alignItems: 'center' }}
                  >
                    <Typography sx={{
                      fontSize: 13, fontWeight: 700, color: 'text.secondary', flexShrink: 0,
                    }}
                    >
                      Looking for something else?
                    </Typography>
                    {allLinks.filter((l) => l.link !== currentPath).map((l) => {
                      const Icon = ICON_MAP[l.key] || PersonOutlineRoundedIcon;
                      return (
                        <Box
                          key={l.key}
                          component="button"
                          type="button"
                          onClick={() => navigate(l.link)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            gap: 0.6,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: 'action.hover',
                            border: '1px solid',
                            borderColor: 'divider',
                            color: 'text.primary',
                            fontSize: 12.5,
                            fontWeight: 600,
                            fontFamily: 'inherit',
                            lineHeight: 1.5,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            transition: 'background-color 0.15s ease, transform 0.15s ease',
                            '&:hover': { bgcolor: 'action.selected', transform: 'translateY(-1px)' },
                          }}
                        >
                          <Icon sx={{ fontSize: 15 }} />
                          {l.name}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Page>
  );
};

export const V2SettingsShell = (props) => (
  <V2ThemeProvider>
    <SettingsShellInner {...props} />
  </V2ThemeProvider>
);
